import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { Router } from '@angular/router';
import { CommonCommunicationService } from 'src/app/core/services/commonCommunication.service';
import { IntegrationService } from 'src/app/core/services/integration.service';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { TranslationService } from 'src/app/core/services/translation.service';
import { PricingService } from 'src/app/core/services/pricing.service';
import { WebSocketService } from 'src/app/core/services/websocket.service';
import { telemetry } from 'src/app/core/telemetry/telemetry';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  styleUrls: ['./topbar.component.scss'],
  standalone: false
})
export class TopbarComponent implements OnInit, OnDestroy {
  public showUserDropdown = false;
  public showNotifications = false;
  public notificationList: any = [];
  public unreadCount: any = 0;
  public activeNotificationsTab = 'all';
  public activeShopId: any;
  public shopDetail: any;
  public isLangLoading: boolean = true;

  environment = environment;
  groupedNotifications: { label: string; items: any[] }[] = [];
  tabUnreadCounts: Record<string, number> = {};
  isSoundMuted: boolean = localStorage.getItem('adminSoundMasterMute') === 'true';

  filterTabs = [
    { key: 'all', label: 'All' },
    { key: 'orders', label: 'Orders' },
    { key: 'inventory', label: 'Inventory' },
    { key: 'others', label: 'Others' }
  ];

  notificationMeta: Record<string, { icon: string; color: string; bgColor: string; route?: string; param?: string }> = {
    ADMIN_NEW_ORDER: { icon: 'shopping_cart', color: '#2363e0', bgColor: '#eff4ff', route: '/orders/order-details' },
    // Pre-orders route to the dedicated pre-book details page, which reads the id from `?id=`
    // (not `?orderId=` like regular orders).
    ADMIN_PRE_ORDER_PLACED: {
      icon: 'event_available',
      color: '#2363e0',
      bgColor: '#eff4ff',
      route: '/orders/prebook-order-details',
      param: 'id'
    },
    ADMIN_ORDER_CANCELLED: { icon: 'cancel', color: '#d92d20', bgColor: '#fef3f2', route: '/orders/order-details' },
    ADMIN_ORDER_DELIVERED: {
      icon: 'check_circle',
      color: '#079b5d',
      bgColor: '#f0fdf4',
      route: '/orders/order-details'
    },
    ADMIN_RETURN_REQUESTED: {
      icon: 'assignment_return',
      color: '#dc6803',
      bgColor: '#fffaeb',
      route: '/orders/return-request-v2'
    },
    ADMIN_LOW_STOCK: { icon: 'inventory', color: '#dc6803', bgColor: '#fffaeb' },
    ADMIN_NEW_CUSTOMER: { icon: 'person_add', color: '#2363e0', bgColor: '#eff4ff', route: '/customers' },
    ADMIN_EDU_APPLICATION: {
      icon: 'school',
      color: '#6941c6',
      bgColor: '#f9f5ff',
      route: '/academic-store/educustomer'
    },
    ADMIN_PAYMENT_FAILED: { icon: 'payment', color: '#d92d20', bgColor: '#fef3f2' },
    ADMIN_REVIEW_POSTED: { icon: 'star', color: '#dc6803', bgColor: '#fffaeb', route: '/reviews' },
    NORMAL_NOTIFICATION: { icon: 'notifications', color: '#2363e0', bgColor: '#eff4ff', route: '/orders/order-details' }
  };

  userDetails: any = {};
  nameCharacter: any = '';
  shopIdsList: any = [];
  public shopList: any = [];
  totalNotifications: any = 0;
  modulePermissionList: any;
  showPOS: any = false;
  showStoreSetting: any = false;
  hasNotificationPermission: boolean = false;
  showLanguageDropdown: boolean = false;
  leftPosition: string = '220px';
  Lang = 'en';

  languages = [
    {
      code: 'en',
      label: 'English',
      flag: 'https://flagcdn.com/w20/gb.png'
    },
    {
      code: 'ar',
      label: 'Arabic',
      flag: 'https://flagcdn.com/w20/ae.png'
    }
  ];
  open: boolean = false;
  selectedLang: any = this.languages[0];
  direction: any = 'ltr';
  private destroy$ = new Subject<void>();

  constructor(
    private storageService: StorageService,
    private router: Router,
    private ccs: CommonCommunicationService,
    private integrationService: IntegrationService,
    private userService: UserService,
    public translate: TranslateService,
    private translateService: TranslationService,
    private pricingService: PricingService,
    private wsService: WebSocketService
  ) {
    this.translate.addLangs(['en', 'ar']);
    this.translate.setDefaultLang('en');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.lang-wrapper')) {
      this.open = false;
    }
  }

  async ngOnInit() {
    this.direction = localStorage.getItem('dir') ?? 'ltr';

    const permissions = this.ccs.rolePermission$.value;
    this.hasNotificationPermission = !!permissions?.['store_notification']?.['view'];

    this.ccs.isSidebarCollapsed$.pipe(takeUntil(this.destroy$)).subscribe((isCollapsed) => {
      this.leftPosition = isCollapsed ? '55px' : '220px';
    });

    this.ccs
      .getUserData()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.userDetails = res;
          if (this.userDetails.name?.length) {
            this.nameCharacter = this.userDetails.name[0];
          }
        }
        this.userBarState();
        this.notificationBarState();
      });

    this.ccs
      .getActiveShopId()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          this.activeShopId = res;
          if (this.hasNotificationPermission) {
            this.getNotification();
          }
          this.getShopDetailById();

          // Join socket room and listen for real-time admin notifications
          this.wsService.joinRoom(res);
          this.wsService
            .onAdminNotification()
            .pipe(takeUntil(this.destroy$))
            .subscribe((notification: any) => {
              if (notification) {
                this.playNotificationSound(notification.notificationType);
                // Reload full list from API to get real DB _id for mark-read and navigation
                this.getNotification();
              }
            });
        }
      });

    this.ccs
      .getModuleList()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        if (res) {
          this.modulePermissionList = res;
          this.showPOS = !!this.modulePermissionList?.['new_sale']?.['create'];
          this.showStoreSetting = [
            'business_details',
            'integration',
            'pincode_setting',
            'tax_setting',
            'extra_charges_setting',
            'team_member',
            'role'
          ].some((key) => this.modulePermissionList?.[key]?.['view']);
        }
      });

    const savedLangCode = localStorage.getItem('selectedLang') || 'en';
    this.selectedLang = this.languages.find((lang) => lang.code === savedLangCode) || this.languages[0];

    const hasLocalFile = await this.hasTranslationFile(savedLangCode);

    if (hasLocalFile) {
      this.translateService.setLanguage(savedLangCode);
      this.translate.use(savedLangCode);
      this.isLangLoading = false;
      this.loadGoogleTranslate(false);
    } else {
      this.translate.use(savedLangCode);
      this.loadGoogleTranslate(true);

      setTimeout(() => {
        const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');
        if (select) {
          select.value = savedLangCode;
          select.dispatchEvent(new Event('change'));
        }
      }, 2000);
    }
    this.applyDirection(savedLangCode);
  }

  subscriptionPlan: any;
  daysRemaining: number = 0;
  isTrialExpired: boolean = false;

  getSubscriptionPlan() {
    this.pricingService
      .getPlanBySubscriptionID(this.shopDetail?.subscription?.activeSubscriptionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.subscriptionPlan = res;
        if (this.subscriptionPlan && this.subscriptionPlan.endAt) {
          const endDate = new Date(this.subscriptionPlan.endAt);
          const currentDate = new Date();
          const timeDiff = endDate.getTime() - currentDate.getTime();

          if (timeDiff <= 0) {
            this.isTrialExpired = true;
            this.daysRemaining = 0;
          } else {
            this.isTrialExpired = false;
            this.daysRemaining = Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
          }
        }
      });
  }

  public getShopDetailById() {
    this.userService
      .getShopById(this.activeShopId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res && res.result) {
          this.shopDetail = res.result;
          this.getSubscriptionPlan();
        }
      });
  }

  logout() {
    // Best-effort server-side revocation of the refresh-token family before
    // clearing local storage (fire-and-forget — never block the logout UX).
    const refreshToken = this.ccs.getRefreshToken();
    if (refreshToken) {
      fetch(`${environment.userService}/v1/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      }).catch(() => {});
    }
    this.storageService.clear();
    telemetry.setUser(null);
    this.router.navigate(['/auth/login']);
  }

  toggleNotifications(e: any) {
    e.stopPropagation();
    this.showNotifications = !this.showNotifications;
    if (this.showNotifications) {
      this.ccs.setNotificationBarState('OPEN');
    } else {
      this.ccs.setNotificationBarState('CLOSE');
    }
  }

  navigateToMyAccount(e: any) {
    this.toggleUserDropdown(e);
    this.router.navigate(['account']);
  }
  navigateToGetStarted(e: any) {
    this.ccs.setShopUserData(this.userDetails);
    this.toggleUserDropdown(e);
    this.router.navigate(['/auth/get-started']);
  }
  navigateToStoreSetting(e: any) {
    this.toggleUserDropdown(e);
    this.router.navigate(['/store-setting']);
  }

  toggleUserDropdown(e: any) {
    e.stopPropagation();
    this.showUserDropdown = !this.showUserDropdown;
    if (this.showUserDropdown) {
      this.ccs.setUserBarState('OPEN');
    } else {
      this.ccs.setUserBarState('CLOSE');
    }
  }

  navigateToSetting() {
    this.router.navigate(['store-setting']);
  }

  notificationBarState() {
    this.ccs
      .getNotificationBarState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          if (res === 'OPEN') {
            this.showNotifications = true;
          } else if (res === 'CLOSE') {
            this.showNotifications = false;
          }
        }
      });
  }

  updateReadStatus() {
    if (!this.hasNotificationPermission) return;
    let payload = {
      shopId: this.activeShopId,
      notificationRecipient: 'SHOP'
    };
    this.integrationService
      .updateNotificationReadStatus(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.getNotification();
      });
  }

  userBarState() {
    this.ccs
      .getUserBarState()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        if (res) {
          if (res === 'OPEN') {
            this.showUserDropdown = true;
          } else if (res === 'CLOSE') {
            this.showUserDropdown = false;
          }
        }
      });
  }

  navigateToPreview() {
    // window.open(environment.previewShop +'/'+ this.shopDetail.userName);
    window.open(`https://${this.shopDetail.userName}.${environment.domain}`, '_blank');
  }

  getNotificationMeta(item: any): any {
    const type = item.notificationType || 'NORMAL_NOTIFICATION';
    return this.notificationMeta[type] || this.notificationMeta['NORMAL_NOTIFICATION'];
  }

  updateNotificationStatus(item: any) {
    if (!this.hasNotificationPermission) return;

    // Navigate first — don't block on mark-read API
    const meta = this.getNotificationMeta(item);
    const resourceId = item.notification?.dataBody?.resourceId;
    this.showNotifications = false;

    if (item.notificationType === 'ADMIN_LOW_STOCK') {
      // Low-stock routes by surface: a channel alert opens that channel's Inventory tab; a
      // main-store alert opens the main Inventory list filtered to Low Stock.
      const chId = item.notification?.dataBody?.channelId;
      this.router.navigateByUrl(chId ? `/channel-partners/${chId}?tab=inventory` : '/product/inventory?tab=low-stock');
    } else if (meta.route && resourceId) {
      const queryParam = meta.param || (meta.route.includes('order') ? 'orderId' : 'id');
      this.router.navigateByUrl(`${meta.route}?${queryParam}=${resourceId}`);
    } else if (meta.route) {
      this.router.navigateByUrl(meta.route);
    }

    // Mark as read in background
    if (item._id) {
      this.integrationService
        .updateNotificationStatus({ notificationId: item._id })
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => this.getNotification(),
          error: () => this.getNotification()
        });
    }
  }

  getNotification() {
    let payload = {
      shopId: this.activeShopId,
      notificationRecipient: 'SHOP'
    };
    this.integrationService
      .getNotification(payload, 0, 50)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.notificationList = [];
        this.totalNotifications = 0;
        this.unreadCount = 0;
        if (res && res.data && res.data.length) {
          this.notificationList = res.data;
          this.totalNotifications = res.total;
          let unreadNotification = this.notificationList.filter((item: any) => !item.isOpened);
          this.unreadCount = unreadNotification.length;
        }
        this.buildGroupedNotifications();
        this.buildTabUnreadCounts();
      });
  }

  getFilteredList(): any[] {
    if (this.activeNotificationsTab === 'all') return this.notificationList;
    if (this.activeNotificationsTab === 'orders') {
      return this.notificationList.filter((n: any) =>
        [
          'ADMIN_NEW_ORDER',
          'ADMIN_PRE_ORDER_PLACED',
          'ADMIN_ORDER_CANCELLED',
          'ADMIN_ORDER_DELIVERED',
          'ADMIN_RETURN_REQUESTED',
          'ADMIN_PAYMENT_FAILED',
          'NORMAL_NOTIFICATION'
        ].includes(n.notificationType)
      );
    }
    if (this.activeNotificationsTab === 'inventory') {
      return this.notificationList.filter((n: any) => n.notificationType === 'ADMIN_LOW_STOCK');
    }
    // others
    return this.notificationList.filter((n: any) =>
      ['ADMIN_NEW_CUSTOMER', 'ADMIN_EDU_APPLICATION', 'ADMIN_REVIEW_POSTED'].includes(n.notificationType)
    );
  }

  buildGroupedNotifications(): void {
    const filtered = this.getFilteredList();
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 86400000);
    const weekStart = new Date(today.getTime() - today.getDay() * 86400000);

    const groups: Record<string, any[]> = {
      Today: [],
      Yesterday: [],
      'Earlier This Week': [],
      Older: []
    };

    for (const item of filtered) {
      const d = new Date(item.createdAt);
      if (d >= today) {
        groups['Today'].push(item);
      } else if (d >= yesterday) {
        groups['Yesterday'].push(item);
      } else if (d >= weekStart) {
        groups['Earlier This Week'].push(item);
      } else {
        groups['Older'].push(item);
      }
    }

    this.groupedNotifications = Object.entries(groups)
      .filter(([_, items]) => items.length > 0)
      .map(([label, items]) => ({ label, items }));
  }

  buildTabUnreadCounts(): void {
    const unread = this.notificationList.filter((n: any) => !n.isOpened);
    const orderTypes = [
      'ADMIN_NEW_ORDER',
      'ADMIN_PRE_ORDER_PLACED',
      'ADMIN_ORDER_CANCELLED',
      'ADMIN_ORDER_DELIVERED',
      'ADMIN_RETURN_REQUESTED',
      'ADMIN_PAYMENT_FAILED',
      'NORMAL_NOTIFICATION'
    ];
    const inventoryTypes = ['ADMIN_LOW_STOCK'];

    this.tabUnreadCounts = {
      all: unread.length,
      orders: unread.filter((n: any) => orderTypes.includes(n.notificationType)).length,
      inventory: unread.filter((n: any) => inventoryTypes.includes(n.notificationType)).length,
      others: unread.filter(
        (n: any) => !orderTypes.includes(n.notificationType) && !inventoryTypes.includes(n.notificationType)
      ).length
    };
  }

  onTabChange(tab: string): void {
    this.activeNotificationsTab = tab;
    this.buildGroupedNotifications();
  }

  getRelativeTime(dateStr: string): string {
    const now = Date.now();
    const d = new Date(dateStr).getTime();
    const diff = now - d;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days}d ago`;
    return new Date(dateStr).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  }

  markAllRead(): void {
    this.integrationService
      .updateNotificationReadStatus({
        shopId: this.activeShopId,
        notificationRecipient: 'SHOP'
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => this.getNotification(),
        error: () => {}
      });
  }
  navigateToPOS() {
    this.router.navigateByUrl('/sales/new-sale');
  }

  toggleMasterSound(): void {
    this.isSoundMuted = !this.isSoundMuted;
    localStorage.setItem('adminSoundMasterMute', String(this.isSoundMuted));
  }

  playNotificationSound(eventKey: string): void {
    // Master mute check
    if (this.isSoundMuted) return;

    // Per-event check
    const prefs = JSON.parse(localStorage.getItem('adminSoundPrefs') || '{}');
    if (prefs[eventKey] === false) return;

    try {
      const audio = new Audio('assets/audio/noti.mp3');
      audio.volume = 0.5;
      audio.play().catch(() => {});
    } catch (e) {
      // Audio not supported or blocked by browser policy
    }
  }

  ngOnDestroy(): void {
    if (this.activeShopId) {
      this.wsService.leaveRoom(this.activeShopId);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  googleTranslateElementInit() {
    const google = (window as any)['google'];
    new google.translate.TranslateElement({ pageLanguage: 'en' }, 'google_translate_element');
  }

  changeLang(lang: string) {
    this.translate.use(lang);
    this.Lang = lang;
  }

  toggleLanguageDropdown() {
    this.showLanguageDropdown = !this.showLanguageDropdown;
  }
  async onLanguageChange(langCode: string) {
    if (!langCode) return;

    this.isLangLoading = true; // show loader
    this.selectedLang = this.languages.find((lang) => lang.code === langCode) || this.languages[0];
    localStorage.setItem('selectedLang', langCode);
    this.open = false;
    this.translate.use(langCode);
    let hasLocalFile = await this.hasTranslationFile(langCode);

    if (hasLocalFile) {
      this.translateService.setLanguage(langCode);
    } else {
      let retryCount = 0;
      const maxRetries = 15;

      const waitForGoogleTranslate = () =>
        new Promise<void>((resolve) => {
          const tryChange = () => {
            const translateSelect = document.querySelector<HTMLSelectElement>('.goog-te-combo');
            if (translateSelect) {
              translateSelect.value = langCode;
              translateSelect.dispatchEvent(new Event('change'));
              this.removeGoogleTranslateBanner();
              resolve();
            } else if (retryCount < maxRetries) {
              retryCount++;
              setTimeout(tryChange, 300);
            } else {
              resolve(); // give up but continue
            }
          };
          tryChange();
        });

      // wait for ngx-translate + Google Translate
      await waitForGoogleTranslate();
      setTimeout(() => {
        this.applyDirection(langCode);
        this.isLangLoading = false;
      }, 1000);
    }
    window.location.reload();
  }
  applyDirection(lang: string) {
    const htmlTag = document.documentElement;
    const bodyTag = document.body;
    const rtlLangs = ['ar', 'he', 'fa', 'ur', 'ps', 'ku', 'sd'];

    if (rtlLangs.includes(lang)) {
      htmlTag.setAttribute('dir', 'rtl');
      bodyTag.classList.add('rtl');
      bodyTag.classList.remove('ltr');
      this.direction = 'rtl';
      localStorage.setItem('dir', 'rtl');
    } else {
      htmlTag.setAttribute('dir', 'ltr');
      bodyTag.classList.add('ltr');
      bodyTag.classList.remove('rtl');
      this.direction = 'ltr';
    }
  }

  async loadGoogleTranslate(autoTranslate = false) {
    // Prevent multiple script injections
    if (document.getElementById('google-translate-script')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'google-translate-script';
    script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateScriptLoaded';
    document.body.appendChild(script);

    (window as any).googleTranslateScriptLoaded = () => {
      if (autoTranslate) {
        // 🌍 Case: we want Google to translate the page
        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            layout: (window as any).google.translate.TranslateElement.InlineLayout.HORIZONTAL
          },
          'hidden_translate_element'
        );
      } else {
        // 🧠 Case: we only want language list (no translation)
        // Create a fake select manually to extract languages later
        const fakeDiv = document.createElement('div');
        fakeDiv.id = 'hidden_translate_element';
        document.body.appendChild(fakeDiv);

        new (window as any).google.translate.TranslateElement(
          {
            pageLanguage: 'en',
            layout: (window as any).google.translate.TranslateElement.InlineLayout.HORIZONTAL
          },
          'hidden_translate_element'
        );

        document.documentElement.classList.remove('translated-ltr', 'translated-rtl');
        document.body.classList.add('notranslate');
        document.querySelectorAll('iframe[src*="translate.google.com"]').forEach((i) => i.remove());
      }

      setTimeout(() => {
        // Uncomment the code below to load all available languages from Google Translate
        // const select = document.querySelector<HTMLSelectElement>('.goog-te-combo');
        // if (select) {
        //   const langs = Array.from(select.options)
        //     .filter((o) => o.text && o.text !== 'Select Language')
        //     .map((o) => ({ code: o.value, label: o.text }));
        //
        //   if (!langs.some((l) => l.code === 'en')) langs.unshift({ code: 'en', label: 'English' });
        //   this.languages = langs;
        // }

        this.removeGoogleTranslateBanner();
        this.isLangLoading = false;
      }, 1500);
    };
  }

  private removeGoogleTranslateBanner() {
    const intervalId = setInterval(() => {
      document
        .querySelectorAll(
          '#goog-gt-tt, .goog-te-balloon-frame, .goog-tooltip, .goog-text-highlight, .goog-te-banner-frame'
        )
        .forEach((el) => el.remove());
      document.body.style.top = '0px';
    }, 300);
    setTimeout(() => clearInterval(intervalId), 5000);
  }

  private async hasTranslationFile(langCode: string): Promise<boolean> {
    const translationFilePath = `/assets/i18n/${langCode}.json`;

    try {
      const res = await fetch(translationFilePath);
      return res.ok;
    } catch (err) {
      return false;
    }
  }
  navigateToTrial() {
    this.router.navigate(['/pricing']);
  }
  isNotificationFromLast24Hours(createdAt: string | Date): boolean {
    const notificationDate = new Date(createdAt);
    const currentDate = new Date();
    const timeDifference = currentDate.getTime() - notificationDate.getTime();
    const hoursDifference = timeDifference / (1000 * 60 * 60);
    return hoursDifference <= 24;
  }

  trackByIndex(index: number): number {
    return index;
  }
}
